import { useEffect, useMemo } from 'react'
import { isNil } from 'lodash'
import moment from 'moment'
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { Button, Link, Popover, Text, Icon } from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

import { UserTaxEstimate } from '../../userTaxEstimates.slice'
import {
  BreakdownVersion,
  selectCalculatorVersionByTaxEstimateId,
  selectIsSingleStateCalculationByTaxEstimateId,
  selectTaxCalculationByEstimateId,
} from '@/selectors/userTaxCalculation.selectors'

import {
  selectStatusForTaxEstimate,
  selectUserTaxEstimateById,
} from '../../userTaxEstimates.selector'
import { EstimateStatus } from '../../service'

import { createLinkToTaxBreakdown } from './TaxEstimateCta'
import { TypographyStyles, TextColors } from '@/styles'
import { getUserDocumentById } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

const EstimatedPaymentSubtitle = ({
  estimate,
}: {
  estimate: UserTaxEstimate | null
}) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  const userTaxCalculation = useReselector(
    selectTaxCalculationByEstimateId,
    estimate?.id
  )
  const isSingleStateCalc = useReselector(
    selectIsSingleStateCalculationByTaxEstimateId,
    estimate?.id
  )
  const hasBreakdownPage =
    useReselector(selectCalculatorVersionByTaxEstimateId, estimate?.id) !==
    BreakdownVersion.defaultBreakdown
  const isFederal = estimate?.type === 'federal'
  const showPopup = isSingleStateCalc && userTaxCalculation?.calculatedByUserId

  return (
    <div className="mt-1 space-y-1">
      <Popover>
        <Popover.Trigger asChild>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="text-forest cursor-pointer inline-flex items-center"
          >
            estimated payment{' '}
            {showPopup && <Icon icon={faQuestionCircle} className="ml-1" />}
          </Text>
        </Popover.Trigger>
        <Popover.Content className="max-w-[240px] text-sm">
          <Text>
            Your final estimate has been manually adjusted by Heard staff.
          </Text>
        </Popover.Content>
      </Popover>

      {isFederal && hasBreakdownPage && (
        <Text variant={TypographyStyles.BODY_SM} className="text-forest">
          If this looks off, double-check your entries{' '}
          <Link
            variant="secondary"
            href={createLinkToTaxBreakdown(estimate, { incomeExpanded: true })}
            newPage={false}
          >
            here.
          </Link>
        </Text>
      )}
    </div>
  )
}

export const RightColumn = ({
  estimateId,
  toggleOpen,
  setToggleOpen,
}: {
  estimateId?: number
  toggleOpen: boolean
  setToggleOpen: () => void
}) => {
  const dispatch = useAppDispatch()
  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  const status = useReselector(selectStatusForTaxEstimate, estimateId)
  const document = useReselector(getUserDocumentById, estimate?.userDocumentId)

  useEffect(() => {
    dispatch(fetchUserDocuments())
  }, [dispatch])

  const subContent = useMemo(() => {
    switch (status) {
      case EstimateStatus.paid:
        return (
          <>
            <div className="flex items-center justify-end sm:justify-start">
              <Text variant={TypographyStyles.BODY_SM}>
                paid{' '}
                {moment(estimate?.paidAt).format(DATE_FORMATS.DISPLAY_LONG)}
              </Text>
              <Icon icon={faCheck} className="ml-1" color="green" size="1x" />
            </div>

            {document?.signedUrl && (
              <Link
                newPage
                href={document.signedUrl}
                className="flex items-center mt-2 justify-end sm:justify-start"
              >
                <Button variant="link">receipt uploaded</Button>
                <Icon icon={faCheck} className="ml-1" color="green" size="1x" />
              </Link>
            )}

            {toggleOpen && (
              <>
                <Text variant={TypographyStyles.H3} className="mt-3">
                  {formatCurrencyFromCents(estimate?.estimateInCents)}
                </Text>
                <EstimatedPaymentSubtitle estimate={estimate} />
              </>
            )}

            <Text color={TextColors.BRAND} variant={TypographyStyles.H1}>
              {!isNil(estimate?.amountPaidInCents)
                ? formatCurrencyFromCents(estimate.amountPaidInCents)
                : '$ ---'}
            </Text>
          </>
        )

      case EstimateStatus.upcoming:
      case EstimateStatus.awaitingEstimate:
      case EstimateStatus.unpaidWithResponse:
        return <Text variant={TypographyStyles.BODY_SM}>estimate</Text>

      case EstimateStatus.noIncomeTax:
        return <Text variant={TypographyStyles.BODY_SM}>No taxes owed.</Text>

      case EstimateStatus.estimateIsZero:
      case EstimateStatus.unpaidWithoutResponse:
      case EstimateStatus.readyForPayment:
        return <EstimatedPaymentSubtitle estimate={estimate} />

      case EstimateStatus.joinedTooLate:
        return null

      default:
        return status satisfies never
    }
  }, [document?.signedUrl, estimate, status, toggleOpen])

  const estimateColor = useMemo(() => {
    switch (status) {
      case EstimateStatus.readyForPayment:
        return TextColors.ORANGE
      case EstimateStatus.unpaidWithoutResponse:
        return TextColors.RED
      case EstimateStatus.unpaidWithResponse:
        return TextColors.GREEN
      case EstimateStatus.upcoming:
      case EstimateStatus.awaitingEstimate:
      case EstimateStatus.estimateIsZero:
        return TextColors.MUTED
      case EstimateStatus.paid:
      case EstimateStatus.noIncomeTax:
      case EstimateStatus.joinedTooLate:
        return null
      default:
        return status satisfies never
    }
  }, [status])

  return (
    <div className="w-full sm:w-1/3 text-right sm:text-left">
      {estimateColor && (
        <Text
          color={estimateColor}
          variant={TypographyStyles.H1}
          className="w-full"
        >
          {!isNil(estimate?.estimateInCents)
            ? formatCurrencyFromCents(estimate.estimateInCents)
            : '$ ---'}
        </Text>
      )}
      {subContent}
      {status === EstimateStatus.paid && (
        <Button onClick={setToggleOpen} variant="link">
          {toggleOpen ? 'Hide details' : 'Show Details'}
        </Button>
      )}
    </div>
  )
}
